import { Environment } from "../utils/Environment";
import React, { useState, useEffect } from "react";
import { postWithUserFlowParams, postWithUserTableParams } from "../services/fetchWrapper";
import Button from "@mui/material/Button";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import SuccessMessageComponent from "./SuccessMessageComponent";

const apiUrl = Environment.getApiUrl();

interface ProcessRoadScoreRequest {
    job_id: string;
    table_name: string;
    compare_table: string;
    buffer_layer_path: string;
    road_type_column: string;
    num_of_lanes_column: string;
    score_column: string;
    buffer_column: string;
    scnd_buffer_column: string;
    user_input_lane_flag: boolean;
    user_lanes: string[];
    client_id: string;
}

const ProcessRoadScore: React.FC = () => {
    const [roadScoreJson, setRoadScoreJson] = useState<any | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    type RoadColumnOption = { column_name: string };
    const [possibleColumns, setPossibleColumns] = useState<RoadColumnOption[]>([]);
    const [roadTypeColumn, setRoadTypeColumn] = useState<string>("");
    const [numLanesColumn, setNumLanesColumn] = useState<string>("");
    const [compareTable, setCompareTable] = useState<string>("");

    const [uploadStatus, setUploadStatus] = useState<string | null>(null);
    const [statusMessage, setStatusMessage] = useState<string | null>(null);
    const [showSuccess, setShowSuccess] = useState(false);
    const [processingDone, setProcessingDone] = useState(false);

    const flow_name = 'road'

    // Fetch client data on mount
    useEffect(() => {
        const fetchClientData = async () => {
            // Get values from localStorage at function run time
            const username = localStorage.getItem("userName") || "";
            const client_id = localStorage.getItem("client_id") || "";
            const job_id = localStorage.getItem("job_id") || "";
            setLoading(true);
            setError(null);
            try {
                const response = await postWithUserTableParams(`${apiUrl}/get-client-data-for-road-scoring`, {
                    username,
                    client_id,
                    job_id,
                });
                if (!response.ok) {
                    throw new Error("Failed to fetch client data");
                }
                const data = await response.json();
                if (data.status !== 'success' || typeof data.json !== 'object' || data.json === null) {
                    throw new Error("Invalid response format from backend");
                }
                setRoadScoreJson(data.json);
            } catch (err: any) {
                setError(err.message || "Failed to fetch client data");
            } finally {
                setLoading(false);
            }
        };
        fetchClientData();
    }, [apiUrl]);

    // Fetch possible columns for road_type and num_lanes
    useEffect(() => {
        const fetchRoadColumns = async () => {
            // Get values from localStorage at function run time
            const username = localStorage.getItem("userName") || "";
            const client_id = localStorage.getItem("client_id") || "";
            const job_id = localStorage.getItem("job_id") || "";
            setLoading(true);
            setError(null);
            try {
                // Adjust endpoint and payload as needed for your backend
                const response = await postWithUserTableParams(`${apiUrl}/get-road-columns`, {
                    username,
                    client_id,
                    job_id,
                });
                if (!response.ok) {
                    throw new Error("Failed to fetch road columns");
                }
                const data = await response.json();
                if (!Array.isArray(data.possible_columns)) {
                    throw new Error("Invalid response format for road columns");
                }
                setPossibleColumns(data.possible_columns);
                // Optionally auto-select if you want
                setRoadTypeColumn(
                    data.possible_columns.find((c: RoadColumnOption) => /fedfunccls|road_type/i.test(c.column_name))?.column_name || ""
                );
                setNumLanesColumn(
                    data.possible_columns.find((c: RoadColumnOption) => /num_lanes|lanes/i.test(c.column_name))?.column_name || ""
                );
            } catch (err: any) {
                setError(err.message || "Failed to fetch road columns");
            } finally {
                setLoading(false);
            }
        };
        fetchRoadColumns();
    }, [apiUrl]);

    useEffect(() => {
        const fetchClientData = async () => {
            // Get values from localStorage at function run time
            const username = localStorage.getItem("userName") || "";
            const client_id = localStorage.getItem("client_id") || "";
            const job_id = localStorage.getItem("job_id") || "";
            setLoading(true);
            setError(null);
            try {
                const response = await postWithUserFlowParams(`${apiUrl}/get-specific-table`, {
                    username,
                    client_id,
                    job_id,
                    flow_name
                });
                if (!response.ok) {
                    throw new Error("Failed to fetch client data");
                }
                const data = await response.json();
                if (data.status !== 'success' || typeof data.json !== 'object' || data.json === null) {
                    throw new Error("Invalid response format from backend");
                }
                setCompareTable(data.table_name);
            } catch (err: any) {
                setError(err.message || "Failed to fetch client data");
            } finally {
                setLoading(false);
            }
        };
        fetchClientData();
    }, [apiUrl]);

    // Process road scores
    const handleProcessRoadScore = async () => {
        // Get values from localStorage at function run time
        const client_id = localStorage.getItem("client_id") || "";
        const job_id = localStorage.getItem("job_id") || "";
        setLoading(true);
        setError(null);
        setShowSuccess(false);
        setProcessingDone(false);
        try {
            if (!roadScoreJson) {
                throw new Error("No road score JSON to process");
            }
            if (!roadTypeColumn) {
                throw new Error("Please select the road type column.");
            }
            // Save user column selections if needed
            const columnsToSave = [
                {
                    client_id,
                    job_id,
                    column_name: roadTypeColumn,
                    column_value: "",
                    flow_name: "road",
                    description: "User confirmed road type column"
                },
                {
                    client_id,
                    job_id,
                    column_name: numLanesColumn,
                    column_value: "",
                    flow_name: "road",
                    description: "User confirmed number of lanes column"
                }
            ];
            await fetch(`${apiUrl}/save-customer-calc-columns`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ columns: columnsToSave, client_id }),
            });

            const request: ProcessRoadScoreRequest = {
                job_id: job_id,
                table_name: roadScoreJson.table_name,
                compare_table: compareTable,
                buffer_layer_path: roadScoreJson.buffer_layer_path,
                road_type_column: roadTypeColumn,
                num_of_lanes_column: numLanesColumn,
                score_column: roadScoreJson.score_column,
                buffer_column: roadScoreJson.buffer_column,
                scnd_buffer_column: roadScoreJson.scnd_buffer_column,
                user_input_lane_flag: roadScoreJson.user_input_lane_flag,
                user_lanes: roadScoreJson.user_lanes,
                client_id: client_id
            };
            const response = await fetch(`${apiUrl}/road-service-scoring`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(request),
            });
            if (!response.ok) {
                throw new Error("Failed to process road score");
            }
            localStorage.setItem("roadScoreDone", "true");
            setShowSuccess(true);
            setUploadStatus("completed");
            setStatusMessage("Road score processed successfully!");
        } catch (err: any) {
            setShowSuccess(false);
            setUploadStatus("failed");
            setStatusMessage("Check column name");
            setProcessingDone(true);
        } finally {
            setProcessingDone(true);
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                mt: 2,
                p: 2,
                maxWidth: 500,
                width: "100%",
                mx: "auto",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
            }}
        >
            <Typography variant="h5"
                sx={{ mb: 2, fontWeight: 700, textTransform: "uppercase" }}>
                Road Scoring
            </Typography>
            <svg width="292" height="6" viewBox="0 0 292 6" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: 16 }}>
                <path d="M0 3H292" stroke="#8AD019" strokeWidth="6" />
            </svg>
            <Typography sx={{ mb: 3, fontSize: 16 }}>
                The Road Type column should indicate what type of road is present (minor arterial, major arterial, interstate, etc.). The Number of Lanes column is optional and default values will be used if this column is not present. Once scoring is complete, a success message will appear.
            </Typography>
            {showSuccess ? (
                <SuccessMessageComponent />
            ) : (
                processingDone && (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 2 }}>
                        <HighlightOffIcon sx={{ fontSize: 40, color: "#828282" }} />
                        <Typography variant="body2" sx={{ color: "#828282" }}>
                            Scoring did not complete successfully.
                        </Typography>
                    </Box>
                )
            )}
            <Typography variant="body2" sx={{ mb: 1, fontSize: 18, fontWeight: 700, maxWidth: 450, textAlign: "center" }}>
                Road Type Column
            </Typography>
            <Autocomplete
                freeSolo
                options={possibleColumns}
                getOptionLabel={option => typeof option === "string" ? option : option.column_name}
                value={possibleColumns.find(opt => (typeof opt === "string" ? opt : opt.column_name) === roadTypeColumn) || null}
                inputValue={roadTypeColumn}
                onChange={(_, value) => setRoadTypeColumn(value ? (typeof value === "string" ? value : value.column_name) : "")}
                onInputChange={(_, value) => setRoadTypeColumn(value || "")}
                renderInput={params =>
                    <TextField {...params}
                        variant="outlined"
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                backgroundColor: "#F6FFE8",
                                height: 36,
                            },
                            "& .MuiOutlinedInput-input": {
                                padding: "8px 12px",
                            },
                        }}
                    />
                }
                sx={{ mb: 2, width: 300 }}
            />
            {/* <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                If your data does not have a "number of lanes" column, you may leave this field blank, or manually enter the correct column name if needed.
            </Typography> */}
            <Typography variant="body2" sx={{ mb: 1, fontSize: 18, fontWeight: 700, maxWidth: 450, textAlign: "center" }}>
                Number of Lanes Column
            </Typography>
            <Autocomplete
                freeSolo
                options={possibleColumns}
                getOptionLabel={option => typeof option === "string" ? option : option.column_name}
                value={possibleColumns.find(opt => (typeof opt === "string" ? opt : opt.column_name) === numLanesColumn) || null}
                inputValue={numLanesColumn}
                onChange={(_, value) => setNumLanesColumn(value ? (typeof value === "string" ? value : value.column_name) : "")}
                onInputChange={(_, value) => setNumLanesColumn(value || "")}
                renderInput={params =>
                    <TextField {...params}
                        variant="outlined"
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                backgroundColor: "#F6FFE8",
                                height: 36,
                            },
                            "& .MuiOutlinedInput-input": {
                                padding: "8px 12px",
                            },
                        }}
                    />}
                sx={{ mb: 2, width: 300 }}
            />
            <Button
                variant="contained"
                color="primary"
                disabled={loading || !roadScoreJson || !roadTypeColumn}
                onClick={handleProcessRoadScore}
                sx={{ backgroundColor: "#0373B5" }}
            >
                {loading ? "Processing..." : "Process Road scores"}
            </Button>
            {uploadStatus && (
                <Box
                    sx={{
                        mt: 2,
                        p: 2,
                        border: "1px solid #ccc",
                        borderRadius: 2,
                        maxWidth: 500,
                        mx: "auto",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                >
                    <Typography variant="h6">Processing Status</Typography>
                    <Typography variant="body2">
                        Status: {uploadStatus}
                    </Typography>
                    <Typography variant="body2">
                        Message: {statusMessage}
                    </Typography>
                </Box>
            )}
            {error && <div style={{ color: "red", marginTop: 8 }}>{error}</div>}
        </Box>
    );
};

export default ProcessRoadScore;